<template>
  <template-layout :breadCrumbList="breadcrumbComputed">
    <div class="pb-12 px-2 sm:px-4">
      <!-- Top filter bar with panel tabs -->
      <FilterBar
        :tabs="tabs"
        :active-tab="activeTab"
        :scope="scope"
        :scope-options="scopeOptions"
        :area="area"
        :exporting-pdf="exportingPdf"
        @update:active-tab="activeTab = $event"
        @update:scope="scope = $event"
        @update:area="area = $event"
        @apply-area="onApplyArea"
        @add-data="onAddData"
        @download-pdf="exportDashboardPdf"
      />

      <!-- Panels -->
      <div ref="reportRef">
        <OverviewPanel v-if="activeTab === 'overview' && dashboardReady" :area="areaLevel" :scope="scope" />
        <HouseholdPanel v-else-if="activeTab === 'household'" />
        <PersonPanel v-else-if="activeTab === 'person'" />
        <NeetsPanel v-else-if="activeTab === 'neets'" />
        <DisabilityPanel v-else-if="activeTab === 'disability'" />
        <PlaceholderPanel v-else :title="activeTabLabel" />
        <AddedDataPanel
          v-for="p in addedPanels"
          :key="p.id"
          class="mt-6"
          v-bind="p"
          @remove="removePanel(p.id)"
        />
      </div>
    </div>
  </template-layout>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { provinceList } from "@/utils/province-list";
import TemplateLayout from "@/layouts/top-navbar-menu/Main.vue";
import FilterBar from "./container/FilterBar.vue";
import OverviewPanel from "./panels/OverviewPanel.vue";
import HouseholdPanel from "./panels/HouseholdPanel.vue";
import PersonPanel from "./panels/PersonPanel.vue";
import NeetsPanel from "./panels/NeetsPanel.vue";
import DisabilityPanel from "./panels/DisabilityPanel.vue";
import PlaceholderPanel from "./panels/PlaceholderPanel.vue";
import AddedDataPanel from "./container/AddedDataPanel.vue";
import pdfMake from "@/utils/pdf/pdfFonts";
import { buildDashboardPdfContent } from "@/utils/pdf/buildDashboardPdfContent";
import Swal from "sweetalert2";

const tabs = [
  { label: "ภาพรวม", value: "overview" },
  { label: "ครัวเรือน", value: "household" },
  { label: "บุคคล", value: "person" },
  { label: "NEETs", value: "neets" },
  { label: "เด็กพิการ", value: "disability" },
];
const activeTab = ref("overview");
const activeTabLabel = computed(() => tabs.find((t) => t.value === activeTab.value)?.label || "");

const store = useStore();
const scopeOptions = [
  { label: "ครัวเรือน", value: "ครัวเรือน" },
  { label: "รายคน", value: "คน" },
];
const scope = ref(store.getters["dashboardOverviewFilter/scope"] || "ครัวเรือน");
watch(scope, (val) => {
  store.dispatch("dashboardOverviewFilter/setScope", val);
}, { immediate: true });
const area = ref("ทั้งประเทศ");
const areaLevel = ref("ทั้งประเทศ");
const dashboardReady = ref(false);

const initFromAccount = async () => {
  const areaCode = (store.getters["userAuth/getAreaCode"] || "").split(",")[0];
  if (!areaCode) return;

  const tumbolCodeList = store.getters["userAuth/getAreaCodeList"].filter(code => code.length == 6);
  const provCode = areaCode.slice(0, 2);
  const ampCode  = areaCode.length >= 4 ? areaCode.slice(0, 4) : null;
  const tamCode  = (tumbolCodeList.length == 1 && areaCode.length >= 6) ? areaCode : null;

  const sector = provinceList.find((p) => p.code == provCode)?.th.sector;
  if (!sector) return;

  const token = store.getters["userAuth/getToken"];
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json; charset=utf-8" };
  const base = process.env.VUE_APP_BASE_API;

  try {
    const provRes = await axios.get(`${base}refadms/?sector=${sector}`, { headers });
    if (provRes.status !== 200) return;
    const prov = provRes.data.find((p) => p._id.province_ID == provCode);
    if (!prov) return;

    const address = { region: sector, province: prov, amphur: null, tumbol: null, village: null };

    if (ampCode) {
      const ampRes = await axios.get(`${base}refadms/?prov=${provCode}`, { headers });
      if (ampRes.status === 200) {
        const amp = ampRes.data.find((a) => a._id.amphur_ID == ampCode);
        if (amp) {
          address.amphur = amp;
          if (tamCode) {
            const tamRes = await axios.get(`${base}refadms/?prov=${provCode}&amp=${ampCode}`, { headers });
            if (tamRes.status === 200) {
              const tam = tamRes.data.find((t) => t._id.tumbol_ID == tamCode);
              if (tam) address.tumbol = tam;
            }
          }
        }
      }
    }

    store.dispatch("dashboardOverviewFilter/setStore", { address });
  } catch (_) {
    // no-op: fall back to ทั้งประเทศ
  }
};

watch(
  () => store.getters["dashboardOverviewFilter/getAllFilter"]?.address,
  (addr) => {
    const hasAny = addr && (addr.region || addr.province || addr.amphur || addr.tumbol || addr.village);
    if (!hasAny) {
      area.value = "ทั้งประเทศ";
      areaLevel.value = "ทั้งประเทศ"; return;
    }
    if (addr.village) {
      area.value = `หมู่บ้าน${addr.village.village_name}`;
      areaLevel.value = "รายหมู่บ้าน";
    }
    else if (addr.tumbol) {
      area.value = `ตำบล${addr.tumbol.tumbol_name}`;
      areaLevel.value = "รายหมู่บ้าน";
    }
    else if (addr.amphur) { 
      area.value = `อำเภอ${addr.amphur.amphur_name}`;
      areaLevel.value = "รายตำบล";
    }
    else if (addr.province) { 
      area.value = `จังหวัด${addr.province.province_name}`; 
      areaLevel.value = "รายอำเภอ";
    }
    else if (addr.region) { 
      area.value = addr.region;
      areaLevel.value = "รายจังหวัด";
    }
  },
  { deep: true }
);

onMounted(async () => {
  const filterChange = store.getters["dashboardOverviewFilter/filterChange"];
  if (filterChange === null) await initFromAccount();
  dashboardReady.value = true;
});

const onApplyArea = (selection) => {
  areaLevel.value = selection.level_name;
};


const addedPanels = ref([]);
let panelSeq = 0;
const onAddData = (selection) => {
  addedPanels.value.push({ id: ++panelSeq, ...selection });
};
const removePanel = (id) => {
  addedPanels.value = addedPanels.value.filter((p) => p.id !== id);
};

const breadcrumbComputed = reactive([{ name: "Dashboard", active: true }]);

// ดาวน์โหลด .pdf - หา element ที่ export ได้ทุกตัวที่แสดงอยู่ในขณะนี้ (chart/stat tile/list ที่ผูก
// data-* ไว้ตั้งแต่ component ของแต่ละอย่างเอง) แล้วจัดเป็นเอกสารด้วย pdfmake แทนการ capture ทั้งหน้าจอ
// ทำให้ได้ตัวอักษรที่คมชัดและไฟล์เล็กกว่า ส่วนตัวกราฟเองยังเป็นรูปภาพเพราะ ECharts/Chart.js ไม่มี
// รูปแบบ vector ที่ pdfmake วาดเองได้โดยตรง
const reportRef = ref(null);
const exportingPdf = ref(false);
const exportDashboardPdf = async () => {
  if (!reportRef.value || exportingPdf.value) return;
  exportingPdf.value = true;
  try {
    const content = await buildDashboardPdfContent(reportRef.value);

    if (!content.length) {
      Swal.fire({ icon: "info", text: "ไม่พบข้อมูลที่สามารถส่งออกได้ในหน้านี้" });
      return;
    }

    const docDefinition = {
      pageSize: "A4",
      pageMargins: [36, 40, 36, 40],
      defaultStyle: { font: "Kanit", fontSize: 11 },
      styles: {
        header: { fontSize: 16, bold: true, margin: [0, 0, 0, 12] },
        chartTitle: { fontSize: 12, bold: true, margin: [0, 10, 0, 6] },
      },
      content: [
        { text: `${activeTabLabel.value}`, style: "header" },
        ...content,
      ],
    };

    const date = new Date();
    const stamp = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0"), String(date.getHours()).padStart(2, "0"), String(date.getMinutes()).padStart(2, "0")].join("");
    pdfMake.createPdf(docDefinition).download(`dashboard_${activeTab.value}_${stamp}.pdf`);
  } catch (_) {
    Swal.fire({ icon: "warning", text: "สร้างไฟล์ PDF ไม่สำเร็จ โปรดลองอีกครั้ง" });
  } finally {
    exportingPdf.value = false;
  }
};
</script>
